import { useDataMaidEntityActions } from 'api/datamaid';
import { useEntityActions, useEntityExtraFields } from 'api/entity';
import { useFieldTypes } from 'api/fieldEditor';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import { useCurrentModal } from 'components/modal';
import { useNotifications } from 'components/notify';
import Papa from 'papaparse';
import { useMemo, useState } from 'react';
import {
  FinsightEntity,
  FinsightEntityCreateRequest,
  getEntityExtraFieldValue,
} from 'shared';
import { useSWRConfig } from 'swr';
import { sortFields } from 'utils/fields';

type CreateEntityProps = {
  type: string;
  parent?: FinsightEntity;
  moreClicked?: boolean;
};

export type EntityCreateOptions = {
  autofillWithDataMaid?: boolean;
};

export function useCreateEntityController(props: CreateEntityProps) {
  const modalCtx = useCurrentModal();
  const { showNotification } = useNotifications();

  const { autofillEntityFields } = useDataMaidEntityActions();

  const [entity, setEntity] = useState<FinsightEntityCreateRequest>({
    name: '',
    type: props.type,
    extraFields: {},
    parent: props.parent?.id,
  });

  const workspaceId = useWorkspaceId();
  const extraFields = useEntityExtraFields(entity.type);
  const { createEntity } = useEntityActions();

  const [loading, setLoading] = useState(false);
  const { mutate, cache } = useSWRConfig();

  const [error, setError] = useState<string | null>(null);
  const [textInputDialogOpen, setTextInputDialogOpen] = useState(false);
  const [textInput, setTextInput] = useState('');
  const [csvEntities, setCsvEntities] = useState<Array<Record<string, string>>>(
    [],
  );
  const [isTableMode, setIsTableMode] = useState(false);
  // Keep raw CSV text in memory until they create or close
  const [rawCsvText, setRawCsvText] = useState<string>('');

  const entityTypes = useFieldTypes();

  const areValuesValid = useMemo(() => {
    try {
      for (const field of extraFields.data ?? []) {
        getEntityExtraFieldValue(field, entity.extraFields?.[field.name]);
      }

      return true;
    } catch {
      return false;
    }
  }, [entity.extraFields, extraFields.data]);

  const canSubmit = entity.name.length && areValuesValid;

  // Separate required and optional fields
  const sortedFields = sortFields(extraFields.data ?? []);
  const requiredFields = sortedFields.filter((field) => field.schema.required);
  const optionalFields = sortedFields.filter((field) => !field.schema.required);

  // Handle creating multiple entities from CSV
  const handleCreateMultipleEntities = async () => {
    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Parse the raw CSV text fresh when creating (in case it was edited)
      const csvTextToParse = rawCsvText || textInput;
      const result = Papa.parse(csvTextToParse, {
        header: true,
        skipEmptyLines: true,
      });
      const entitiesToCreate = (result.data || []) as Array<
        Record<string, string>
      >;

      for (let i = 0; i < entitiesToCreate.length; i++) {
        const csvEntity = entitiesToCreate[i];

        try {
          const newEntityData: FinsightEntityCreateRequest = {
            name: csvEntity.name || '',
            type: entity.type,
            description: csvEntity.description || '',
            extraFields: {},
            parent: props.parent?.id,
          };

          // Map CSV data to extra fields
          Object.entries(csvEntity).forEach(([columnName, value]) => {
            if (columnName !== 'name' && columnName !== 'description') {
              const field = sortedFields.find((f) => f.name === columnName);
              if (field) {
                newEntityData.extraFields![columnName] = value;
              }
            }
          });

          const response = await createEntity(newEntityData);
          if (response.ok) {
            successCount++;
          } else {
            errorCount++;
            console.error(
              `Failed to create entity ${i + 1}: ${csvEntity.name}`,
            );
          }
        } catch (error) {
          errorCount++;
          console.error(
            `Error creating entity ${i + 1}: ${csvEntity.name}`,
            error,
          );
        }
      }

      // Show results
      if (errorCount === 0) {
        setError(null);
        showNotification(`Successfully created ${successCount} entities`, {
          variant: 'success',
        });
        modalCtx?.closeModal();
      } else if (successCount > 0) {
        setError(
          `Created ${successCount} entities successfully. ${errorCount} failed.`,
        );
        showNotification(
          `Created ${successCount} entities successfully. ${errorCount} failed.`,
          { variant: 'warning' },
        );
        // Still close modal but show partial success message
        setTimeout(() => {
          modalCtx?.closeModal();
        }, 3000);
      } else {
        setError('Failed to create any entities');
        showNotification('Failed to create any entities', { variant: 'error' });
      }

      // Refresh cache
      Array.from(cache.keys())
        .filter(
          (x) =>
            x.includes(`/workspace/${workspaceId}/entities/${entity.type}`) ||
            (props.parent &&
              (x.includes(
                `/workspace/${workspaceId}/entity/${props.parent?.id}/children`,
              ) ||
                x.includes(
                  `/workspace/${workspaceId}/entity/${props.parent?.id}`,
                ))),
        )
        .forEach((key) => mutate(key));

      // Clear raw CSV text after successful creation
      setRawCsvText('');
      setTextInput('');
    } catch (e) {
      setError('Failed to create entities');
      showNotification('Failed to create entities', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle text input parsing
  const handleTextInputSubmit = () => {
    try {
      // Store the raw CSV text
      setRawCsvText(textInput);

      const result = Papa.parse(textInput, {
        header: true,
        skipEmptyLines: true,
      });

      if (result.data && result.data.length > 0) {
        if (result.data.length === 1) {
          // Single row - update current entity
          const csvData = result.data[0] as Record<string, string>;

          const newEntity = { ...entity };
          const newExtraFields = { ...entity.extraFields };

          // Only update fields that are present in the CSV
          Object.entries(csvData).forEach(([columnName, value]) => {
            if (columnName === 'name') {
              newEntity.name = value;
            } else if (columnName === 'description') {
              newEntity.description = value;
            } else {
              // Check if this column corresponds to an existing field
              const field = sortedFields.find((f) => f.name === columnName);
              if (field) {
                newExtraFields[columnName] = value;
              }
            }
          });

          newEntity.extraFields = newExtraFields;
          setEntity(newEntity);
          setTextInputDialogOpen(false);
          // Clear raw CSV text for single row mode (not using table preview)
          setRawCsvText('');
        } else {
          // Multiple rows - switch to table mode
          const entities = result.data as Array<Record<string, string>>;
          setCsvEntities(entities);
          setIsTableMode(true);
          setTextInputDialogOpen(false);
          // Keep rawCsvText for table mode so they can edit it again
        }
      }
    } catch (error) {
      console.error('Error parsing CSV:', error);
      setError('Invalid CSV format');
    }
  };

  // Convert entity to CSV
  const convertEntityToCSV = () => {
    const data = {
      name: entity.name || '',
      description: entity.description || '',
      ...Object.fromEntries(
        sortedFields.map((field) => [
          field.name,
          entity.extraFields?.[field.name] !== undefined &&
          entity.extraFields?.[field.name] !== null
            ? String(entity.extraFields[field.name])
            : '',
        ]),
      ),
    };

    return Papa.unparse([data]);
  };

  // Handle opening the text input dialog
  const handleOpenTextInput = () => {
    const csv = convertEntityToCSV();
    setTextInput(csv);
    setTextInputDialogOpen(true);
  };

  // Handle creating a single entity
  const handleCreateEntity = async (options: EntityCreateOptions) => {
    setLoading(true);
    try {
      const response = await createEntity(entity);
      const entityId = await response.text();
      if (options.autofillWithDataMaid && entityId) {
        await autofillEntityFields(entityId);
      }
      if (!response.ok) throw new Error();

      showNotification('Entity created successfully', {
        variant: 'success',
        action:
          entityId && workspaceId
            ? {
                label: 'View',
                href: `/workspace/${workspaceId}/entity/${entityId}`,
              }
            : undefined,
      });
      modalCtx?.closeModal();

      Array.from(cache.keys())
        .filter(
          (x) =>
            x.includes(`/workspace/${workspaceId}/entities/${entity.type}`) ||
            (props.parent &&
              (x.includes(
                `/workspace/${workspaceId}/entity/${props.parent?.id}/children`,
              ) ||
                x.includes(
                  `/workspace/${workspaceId}/entity/${props.parent?.id}`,
                ))),
        )
        .forEach((key) => mutate(key));
    } catch (e) {
      setError('Failed to create entity');
      showNotification('Failed to create entity', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Handle CSV entities editing
  const handleEditCsvEntities = () => {
    // Use the raw CSV text if available, otherwise fall back to converting entities
    if (rawCsvText) {
      setTextInput(rawCsvText);
    } else {
      // Fallback: convert entities back to CSV (shouldn't happen in normal flow)
      const csvData = csvEntities.map((entity) => ({
        name: entity.name || '',
        description: entity.description || '',
        ...Object.fromEntries(
          sortedFields.map((field) => [field.name, entity[field.name] || '']),
        ),
      }));
      const csv = Papa.unparse(csvData);
      setTextInput(csv);
    }
    setTextInputDialogOpen(true);
  };

  // Handle canceling table mode
  const handleCancelTableMode = () => {
    setIsTableMode(false);
    setCsvEntities([]);
    setRawCsvText('');
    setTextInput('');
  };

  // Handle closing the CSV dialog
  const handleCloseCsvDialog = () => {
    setTextInputDialogOpen(false);
    // Don't clear rawCsvText here - keep it in case they want to edit again
    // It will be cleared when they cancel table mode or create entities
  };

  return {
    // State
    entity,
    setEntity,
    loading,
    error,
    setError,
    textInputDialogOpen,
    setTextInputDialogOpen,
    textInput,
    setTextInput,
    csvEntities,
    isTableMode,

    // Computed values
    canSubmit,
    sortedFields,
    requiredFields,
    optionalFields,
    entityTypes,

    // Handlers
    handleCreateMultipleEntities,
    handleTextInputSubmit,
    handleOpenTextInput,
    handleCreateEntity,
    handleEditCsvEntities,
    handleCancelTableMode,
    handleCloseCsvDialog,

    // Modal context
    modalCtx,
  };
}
